import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MetaTags } from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import ReactDrawer from 'react-drawer';
import 'react-drawer/lib/react-drawer.css';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import { DashBreadcrumb } from '../../components/Common/Breadcrumb';
import DHPagination from '../../components/Pagination';
import {
  exportPaymentFailedOrders,
  fetchPaymentFailedOrders,
  fetchPaymentFailedOrdersSummary,
} from './actions/action';
import PaymentFailedOrdersFilters from './components/PaymentFailedOrdersFilters';
import PaymentFailedOrdersSummary from './components/PaymentFailedOrdersSummary';
import PaymentFailedOrdersTrend from './components/PaymentFailedOrdersTrend';
import PaymentFailedOrdersList from './components/PaymentFailedOrdersList';
import PaymentFailedOrderDetail from './components/PaymentFailedOrderDetail';
import { buildPaymentFailedOrdersCsv, downloadCsv } from './utils/csv';
import { DEMO_ROWS, filterDemoRows, buildDemoSummary } from './utils/demoData';
import './components/PaymentFailedOrders.css';

// The top bar stores 'all' when no single country is selected - the API wants ''
const normaliseCountry = (value) => {
  const code = (value || '').toLowerCase();
  return code === 'all' ? '' : code;
};

// Scope the list to the country picked in the top bar, as the other modules do
const buildDefaultFilters = () => ({
  search: '',
  actionStatus: [],
  triggerReason: '',
  recordState: 'all',
  country: normaliseCountry(localStorage.getItem('WEBSITE_COUNTRY')),
  dateFrom: '',
  dateTo: '',
});

// Filters as the API expects them - shared by list, summary and export
const toApiFilters = (filters) => ({
  search: filters.search,
  action_status: filters.actionStatus,
  trigger_reason: filters.triggerReason,
  record_state: filters.recordState,
  country: filters.country,
  date_from: filters.dateFrom,
  date_to: filters.dateTo,
});

const PaymentFailedOrders = () => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(buildDefaultFilters);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedRecord, setSelectedRecord] = useState(null);
  // Preview switch for the design while the API is being built - dev builds only
  const isDevBuild = process.env.NODE_ENV === 'development';
  const [demoMode, setDemoMode] = useState(
    () => isDevBuild && localStorage.getItem('PF_DEMO') === '1',
  );
  const [demoRecords, setDemoRecords] = useState(DEMO_ROWS);

  const {
    list = [],
    totalRecords = 0,
    totalPages: apiTotalPages = 0,
    summary,
    isListLoading,
    isSummaryLoading,
    isExporting,
    listError,
  } = useSelector((state) => state.paymentFailedOrders) || {};

  // Follow the top bar country picker without waiting for its page reload
  const selectedCountry = useSelector((state) => state.country?.country);
  useEffect(() => {
    const country = normaliseCountry(
      selectedCountry || localStorage.getItem('WEBSITE_COUNTRY'),
    );
    setFilters((previous) =>
      previous.country === country ? previous : { ...previous, country },
    );
    setPage(1);
  }, [selectedCountry]);

  const apiFilters = useMemo(() => toApiFilters(filters), [filters]);
  const filterKey = JSON.stringify(apiFilters);

  const loadData = useCallback(
    (currentPage, currentPerPage, currentFilters) => {
      fetchPaymentFailedOrders({
        params: {
          ...currentFilters,
          page: currentPage,
          per_page: currentPerPage,
        },
        dispatch,
      }).catch(() => {});
      fetchPaymentFailedOrdersSummary({
        params: { ...currentFilters },
        dispatch,
      }).catch(() => {});
    },
    [dispatch],
  );

  // Typing in the search / reason boxes should not fire a request per keystroke
  useEffect(() => {
    if (demoMode) return undefined;
    const delay = setTimeout(() => {
      loadData(page, perPage, JSON.parse(filterKey));
    }, 300);
    return () => clearTimeout(delay);
  }, [filterKey, page, perPage, loadData, demoMode]);

  const demoAll = useMemo(
    () => (demoMode ? filterDemoRows(apiFilters, demoRecords) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [demoMode, filterKey, demoRecords],
  );

  const handleDemoSave = (id, patch) => {
    setDemoRecords((previous) =>
      previous.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  const handleFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(buildDefaultFilters());
    setPage(1);
  };

  const handleRefresh = () => loadData(page, perPage, apiFilters);

  const handleExport = async () => {
    if (demoMode) {
      downloadCsv(
        `payment-failed-orders-demo-${moment().format('YYYYMMDD-HHmm')}.csv`,
        buildPaymentFailedOrdersCsv(demoAll),
      );
      return;
    }
    try {
      const response = await exportPaymentFailedOrders({
        params: { ...apiFilters },
        dispatch,
      });
      const payload = response?.data;
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      if (!rows.length) {
        toast.error('Nothing to export for the current filters.');
        return;
      }
      downloadCsv(
        `payment-failed-orders-${moment().format('YYYYMMDD-HHmm')}.csv`,
        buildPaymentFailedOrdersCsv(rows),
      );
      toast.success(`Exported ${rows.length} records.`);
    } catch (error) {
      toast.error('Failed to export the report.');
    }
  };

  const startIndex = (page - 1) * perPage;
  const rows = demoMode ? demoAll.slice(startIndex, startIndex + perPage) : list;
  const rowCount = demoMode ? demoAll.length : totalRecords || 0;
  const summaryData = demoMode ? buildDemoSummary(demoAll) : summary;
  // total_pages from the API when it sends one, otherwise derived from the record
  // count - and never fewer pages than the rows themselves imply.
  const totalPages = Math.max(
    1,
    demoMode ? 0 : apiTotalPages,
    Math.ceil(rowCount / perPage),
    rows?.length === perPage ? page + 1 : page,
  );

  return (
    <React.Fragment>
      <div className="page-content pf-page">
        <MetaTags>
          <title>Payment Failed Orders</title>
        </MetaTags>
        <Container fluid>
          <Row className="align-items-center">
            <Col>
              <DashBreadcrumb title="Orders" breadcrumbItem="Payment Failed Orders" />
            </Col>
            <Col xs="auto" className="mb-2">
              {isDevBuild && (
                <div className="form-check form-switch d-inline-block me-3 align-middle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pfDemoMode"
                    checked={demoMode}
                    onChange={(e) => {
                      setDemoMode(e.target.checked);
                      localStorage.setItem('PF_DEMO', e.target.checked ? '1' : '0');
                      setPage(1);
                    }}
                  />
                  <label className="form-check-label pf-cell-muted" htmlFor="pfDemoMode">
                    Demo data
                  </label>
                </div>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={handleRefresh}
                disabled={isListLoading}
              >
                <i className="mdi mdi-refresh me-1"></i>
                Refresh
              </button>
              <button
                type="button"
                className="btn btn-sm btn-dark"
                onClick={handleExport}
                disabled={isExporting}
              >
                <i className="mdi mdi-download me-1"></i>
                {isExporting ? 'Exporting…' : 'Export CSV'}
              </button>
            </Col>
          </Row>

          <PaymentFailedOrdersSummary
            summary={summaryData}
            isLoading={demoMode ? false : isSummaryLoading}
          />

          <PaymentFailedOrdersTrend summary={summaryData} />

          <PaymentFailedOrdersFilters
            filters={filters}
            onChange={handleFiltersChange}
            onReset={handleReset}
          />

          <div className="pf-kpi-card p-0">
            <PaymentFailedOrdersList
              rows={rows}
              isLoading={demoMode ? false : isListLoading}
              error={demoMode ? '' : listError}
              startIndex={startIndex}
              onRowClick={(row) => setSelectedRecord(row)}
            />

            {rows?.length > 0 ? (
              <DHPagination
                totalPages={totalPages}
                currentPage={page - 1}
                setPaginationValue={(value) => setPage(Number(value.page) + 1)}
                setLimitValue={(value) => {
                  setPerPage(Number(value.limit));
                  setPage(1);
                }}
              />
            ) : null}
          </div>
        </Container>

        <ReactDrawer
          open={Boolean(selectedRecord)}
          position="right"
          onClose={() => setSelectedRecord(null)}
        >
          {selectedRecord && (
            <PaymentFailedOrderDetail
              key={selectedRecord.id}
              record={selectedRecord}
              onClose={() => setSelectedRecord(null)}
              onUpdated={handleRefresh}
              onDemoSave={handleDemoSave}
            />
          )}
        </ReactDrawer>
      </div>

      <Toaster position="top-right" reverseOrder={false} />
    </React.Fragment>
  );
};

export default PaymentFailedOrders;
